import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import StarRounded from '@mui/icons-material/StarRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';

import PageHeader from '../components/common/PageHeader';
import StatusBadge from '../components/common/StatusBadge';
import { getBankersOverview } from '../services/userService';
import { STATUS_COLORS } from '../config/constants';

const BankersPage = () => {
  const navigate = useNavigate();
  const [bankers, setBankers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchBankers = async () => {
      try {
        setLoading(true);
        const res = await getBankersOverview();
        setBankers(res.data?.data?.bankers || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load bankers');
      } finally {
        setLoading(false);
      }
    };
    fetchBankers();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return bankers;
    const q = search.toLowerCase();
    return bankers.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.bankName?.toLowerCase().includes(q) ||
        b.bankBranch?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q)
    );
  }, [bankers, search]);

  const getDaysSince = (date) => {
    if (!date) return '-';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Banker Overview" subtitle="View all bankers and their assigned students" />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by banker name, bank, branch, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: '#9CA3AF', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB' } }}
        />
      </Paper>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Total Bankers</Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#1F2937' }}>{bankers.length}</Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Active Bankers</Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#10B981' }}>{bankers.filter((b) => b.isActive).length}</Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Total Assigned</Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#6B5CE7' }}>{bankers.reduce((sum, b) => sum + (b.totalStudents || 0), 0)}</Typography>
        </Paper>
      </Box>

      {/* Banker List */}
      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#9CA3AF' }}>
            {search ? 'No bankers match your search' : 'No bankers found'}
          </Typography>
        </Paper>
      ) : (
        filtered.map((banker) => (
          <Accordion key={banker._id} sx={{ mb: 1.5, '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <AccordionSummary expandIcon={<ExpandMoreRounded />} sx={{ px: 3, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: banker.isActive ? '#6B5CE7' : '#9CA3AF', width: 40, height: 40, fontSize: '0.875rem', fontWeight: 700 }}>
                  {banker.name?.charAt(0)?.toUpperCase() || 'B'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#1F2937',
                        cursor: 'pointer',
                        '&:hover': { color: '#1A73E8', textDecoration: 'underline' },
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/bankers/${banker._id}`); }}
                    >
                      {banker.name}
                    </Typography>
                    <Chip
                      icon={<StarRounded sx={{ fontSize: '14px !important', color: '#D97706 !important' }} />}
                      label="Primary"
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700, '& .MuiChip-label': { px: 0.5 } }}
                    />
                    {!banker.isActive && (
                      <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                    )}
                    {(banker.teamMembers?.length || 0) > 0 && (
                      <Chip
                        icon={<GroupsRounded sx={{ fontSize: '14px !important' }} />}
                        label={`${banker.teamMembers.length} Team`}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha('#1A73E8', 0.1), color: '#1A73E8', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.25 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccountBalanceOutlined sx={{ fontSize: 14 }} /> {banker.bankName || '-'}
                    </Typography>
                    {banker.bankBranch && (
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                        {banker.bankBranch}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mr: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6B7280' }}>
                    <PhoneOutlined sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontSize: '0.8rem' }}>{banker.phone || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6B7280' }}>
                    <EmailOutlined sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontSize: '0.8rem' }}>{banker.email}</Typography>
                  </Box>
                  <Chip
                    icon={<PersonOutlined sx={{ fontSize: '15px !important' }} />}
                    label={`${banker.totalStudents || 0} Students`}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: alpha('#6B5CE7', 0.1), color: '#6B5CE7' }}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              {/* Status breakdown chips */}
              {banker.statusCounts && Object.keys(banker.statusCounts).length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {Object.entries(banker.statusCounts).map(([status, count]) => (
                    <Chip
                      key={status}
                      label={`${status}: ${count}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: alpha(STATUS_COLORS[status] || '#9CA3AF', 0.1),
                        color: STATUS_COLORS[status] || '#9CA3AF',
                      }}
                    />
                  ))}
                </Box>
              )}

              {banker.students?.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>File No.</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Student Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Course / Country</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Loan Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Days</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {banker.students.map((student) => (
                        <TableRow
                          key={student._id}
                          hover
                          sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                          onClick={() => navigate(`/students/${student._id}`)}
                        >
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C7BE5' }}>
                            {student.fileNumber || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#1F2937', fontWeight: 500 }}>
                            {student.name}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            {[student.course, student.country].filter(Boolean).join(' / ') || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                            {formatAmount(student.loanAmount)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={student.status} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            {getDaysSince(student.assignedToBankerAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', textAlign: 'center', py: 2 }}>
                  No students assigned to this banker
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default BankersPage;
